import { useAppStore } from '../../../../store/appStore';
import { MissionMap } from '../map/missionMap/MissionMap';
import { classes, extend, media, stylesheet } from 'typestyle';
import { Gray100, Gray20, Loader, typeBase14RegularProperties, typeCompact14MediumProperties } from '@traceair/tds';
import { MissionWindowPanoramaLocations } from './projectInfo/panoramas/MissionWindowPanoramaLocations';
import React from 'react';
import { CheckFlightAreaButton } from './projectInfo/buttons/CheckFlightAreaButton';
import { ExistingGcpsButton } from './projectInfo/buttons/ExistingGcpsButton';
import { BREAKPOINT_SMALL } from '../../../layout/styles';
import { MissionWindowGcpRequest } from './projectInfo/MissionWindowGcpRequest';
import { GcpRequestStatus } from '../../../../consts/gcpRequestStatus';
import { useMissionAttributes } from './projectInfo/useMissionAttributes';
import { SiteUpdatedTooltip } from './projectInfo/SiteUpdatedTooltip';
import { MissionWindowGcpGuides } from './projectInfo/MissionWindowGcpGuides';
import { MissionWindowGcpMaintenance } from './projectInfo/MissionWindowGcpMaintenance';
import { MissionWindowAltaTopo } from './projectInfo/MissionWindowAltaTopo';

const css = stylesheet({
  container: {
    paddingTop: 12,
  },
  block: {
    $nest: {
      '&:not(:last-child)': {
        paddingBottom: 24,
        borderBottom: `1px solid ${Gray20}`,
        marginBottom: 24,
      },
    },
  },
  gcpRequestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  splitSection: extend(
    {
      display: 'flex',
      gap: 40,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        alignItems: 'flex-start',
        flexDirection: 'column',
      },
    ),
  ),
  splitSectionGcpRequest: media(
    { maxWidth: BREAKPOINT_SMALL },
    {
      gap: 16,
    },
  ),
  heading: {
    margin: '0 0 12px',
    ...typeCompact14MediumProperties,
  },
  gcpRequestNote: extend(
    {
      margin: '-5px 0 24px',
      color: Gray100,
      ...typeBase14RegularProperties,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        marginBottom: 16,
      },
    ),
  ),
  flightArea: {
    position: 'relative',
  },
  updateIndicator: {
    position: 'absolute',
    top: 40,
    left: 8,
    zIndex: 2,
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 150,
  },
  attributes: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  attributeColumnOnDesktop: extend(
    {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        display: 'grid',
        gridTemplateColumns: '100px 1fr',
        gap: 32,
      },
    ),
  ),
  attribute: extend(
    {
      display: 'grid',
      gridTemplateColumns: '150px 1fr',
      gap: 32,
    },
    media(
      {
        maxWidth: BREAKPOINT_SMALL,
      },
      {
        gridTemplateColumns: '100px 1fr',
      },
    ),
  ),
  attributeHeading: {
    color: Gray100,
  },
  attributeNote: {
    color: Gray100,
    marginTop: 6,
  },
  commentLabel: extend(
    {
      marginTop: 24,
      marginBottom: 8,
    },
    media(
      { maxWidth: BREAKPOINT_SMALL },
      {
        display: 'none',
      },
    ),
  ),
  comment: extend(
    {
      lineHeight: '20px',
    },
    media(
      { maxWidth: BREAKPOINT_SMALL },
      {
        marginTop: 16,
      },
    ),
  ),
});

export const MissionWindowProjectInfo = () => {
  const { selectedMission, getGcpRequest } = useAppStore();
  if (!selectedMission) return null;

  const { customer, site, schedule, gcpPlacementService } = selectedMission;
  const { projectInfo } = site;
  const gcpRequest = getGcpRequest(customer.id, site.id);

  const hasActiveGcpRequest = !!gcpRequest && gcpRequest.status === GcpRequestStatus.OPEN;
  const { srsAttributes, otherAttributes } = useMissionAttributes(site);

  const altaTopoFolder = schedule?.altaTopoExchangeFolder ?? gcpPlacementService?.altaTopoExchangeFolder ?? null;

  return (
    <div className={css.container}>
      <section className={classes(css.splitSection, css.block)}>
        <div className={css.flightArea}>
          <h3 className={css.heading}>Flight Area</h3>
          <SiteUpdatedTooltip className={css.updateIndicator} site={site} />
          <MissionMap customer={customer} site={site} schedule={schedule} alwaysRender inOpenMission />
          <CheckFlightAreaButton customer={customer} site={site} />
          {!schedule?.contractContainsRfmm && <ExistingGcpsButton customer={customer} site={site} />}
        </div>

        <div>
          <h3 className={css.heading}>360 Panoramas</h3>
          <MissionWindowPanoramaLocations panoramaLocations={projectInfo.parsedPanoramaLocations} />
        </div>
      </section>

      {gcpRequest === undefined ? (
        <div className={css.loader}>
          <Loader />
        </div>
      ) : (
        <>
          {!!schedule?.contractContainsRfmm && (
            <section className={css.block}>
              <MissionWindowGcpMaintenance customer={customer} site={site} schedule={schedule} />
            </section>
          )}

          {!!altaTopoFolder && (
            <section className={css.block}>
              <MissionWindowAltaTopo altaTopoExchangeFolder={altaTopoFolder} />
            </section>
          )}

          {hasActiveGcpRequest && (
            <section className={css.block}>
              <header className={css.gcpRequestHeader}>
                <div>
                  <div className={css.heading}>GCP Request</div>
                  <div className={css.gcpRequestNote}>Place or fix GCPs and take photos of each one</div>
                </div>

                {!schedule?.contractContainsRfmm && <MissionWindowGcpGuides />}
              </header>

              <div className={classes(css.splitSection, css.splitSectionGcpRequest)}>
                <MissionWindowGcpRequest gcpRequest={gcpRequest} />

                {srsAttributes.length > 0 && (
                  <div className={css.attributes}>
                    {srsAttributes.map(({ key, value }) => (
                      <div key={key} className={css.attributeColumnOnDesktop}>
                        <div className={css.attributeHeading}>{key}</div>
                        <div>{value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {!!gcpRequest?.commentForPilot && (
                <div>
                  <div className={classes(css.attributeHeading, css.commentLabel)}>Comment</div>
                  <div className={css.comment}>{gcpRequest.commentForPilot}</div>
                </div>
              )}
            </section>
          )}

          {otherAttributes.length > 0 && (
            <section className={css.block}>
              <div className={css.attributes}>
                {otherAttributes.map(({ key, value, note }) => (
                  <div key={key} className={css.attribute}>
                    <div className={css.attributeHeading}>{key}</div>
                    <div>
                      {value}
                      {!!note && <div className={css.attributeNote}>{note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!hasActiveGcpRequest && srsAttributes.length > 0 && (
            <section className={classes(css.block, css.attributes)}>
              {srsAttributes.map(({ key, value }) => (
                <div key={key} className={css.attribute}>
                  <div className={css.attributeHeading}>{key}</div>
                  <div>{value}</div>
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
};
